import type {Elements, IContentItem} from '@kontent-ai/delivery-sdk'
import {useRouter} from 'next/router'
import {clsx} from 'clsx'
import {addLocalePrefix} from '@/utilities/add-locale-prefix'
import type {Block, Tersed} from '@/services/kontent-ai/types'
import {CardImage} from '@/products/flexible-web-toolkit/components/card/card-image'
import {CardFooter} from '@/products/flexible-web-toolkit/components/card/card-footer'
import {Card} from '@/products/flexible-web-toolkit/components/card'
import {ReusableCTAButtonComponent} from '../cta-button'
import type {CtaButtonContentItem} from '../cta-button'
import {MikMakButtonBlock} from '@/products/where-to-buy/mikmak/mikmak-button'
import type {MikmakButtonContentItem} from '@/products/where-to-buy/mikmak/mikmak-button'

export type EnhancedCardContentItem = IContentItem<{
	title: Elements.TextElement
	image: Elements.AssetsElement
	imageOnLeft: Elements.MultipleChoiceElement
	content: Elements.RichTextElement
	cardContentInCenter: Elements.MultipleChoiceElement
	button: Elements.LinkedItemsElement<
		CtaButtonContentItem | MikmakButtonContentItem
	>
	stackButtons: Elements.MultipleChoiceElement
	hasTitleLink: Elements.MultipleChoiceElement
	titleUrl: Elements.TextElement
	makeTheImageAClickableLink: Elements.MultipleChoiceElement
	imageClickDestinationUrl: Elements.TextElement
	removeBorders: Elements.MultipleChoiceElement
	openInNewTab: Elements.MultipleChoiceElement
}>

export const EnhancedCardBlock: Block<EnhancedCardContentItem> = ({
	block: {
		elements: {
			button,
			content,
			hasTitleLink,
			image,
			imageOnLeft,
			makeTheImageAClickableLink,
			removeBorders,
			stackButtons,
			title,
			titleUrl,
			cardContentInCenter,
			imageClickDestinationUrl,
		},
		system: {codename},
	},
	...context
}) => {
	const {locale = ''} = useRouter()
	let clickableImageUrl = ''
	const firstButtonUrl =
		button[0]?.system.type === 'cta_button'
			? (button[0] as unknown as Tersed<CtaButtonContentItem>).elements
					.url
			: undefined
	if (makeTheImageAClickableLink[0]?.codename === 'yes') {
		if (imageClickDestinationUrl !== '') {
			clickableImageUrl = addLocalePrefix(
				imageClickDestinationUrl,
				locale
			)
		} else if (firstButtonUrl) {
			clickableImageUrl = addLocalePrefix(firstButtonUrl, locale)
		}
	}
	const mobileFirstButtonUrl = firstButtonUrl
		? addLocalePrefix(firstButtonUrl, locale)
		: null
	const mobileHref =
		hasTitleLink[0]?.codename === 'yes' && titleUrl !== ''
			? addLocalePrefix(titleUrl, locale)
			: mobileFirstButtonUrl
	return (
		<Card
			cardContentInCenter={cardContentInCenter[0]?.codename === 'yes'}
			className={clsx(
				removeBorders[0]?.codename === 'yes' ? undefined : 'p-4',
				`h-full flex-none text-black`
			)}
			data-kontent-element-codename="enhanced_card"
			imageOnLeft={imageOnLeft[0]?.codename === 'yes'}
			mobileOnlyHref={mobileHref}
			mobileOnlyHrefLabel={title}
			removeBorders={removeBorders[0]?.codename === 'yes'}
			title={
				<span data-kontent-element-codename="card_title">{title}</span>
			}
		>
			{image[0] ? (
				<CardImage
					alt={image[0].description}
					className={clsx(
						'h-48',
						removeBorders[0]?.codename === 'yes'
							? 'object-cover'
							: '',
						codename.startsWith('brand')
							? 'object-cover'
							: 'object-contain'
					)}
					data-kontent-element-codename="image"
					imageUrl={clickableImageUrl}
					src={image[0].url}
				/>
			) : null}
			<div
				className="content m-0 w-full break-words"
				dangerouslySetInnerHTML={{__html: content.value}}
				data-kontent-element-codename="text"
			/>
			<CardFooter>
				<>
					{button.map((b, index, arr) =>
						b.system.type === 'cta_button' ? (
							<ReusableCTAButtonComponent
								block={
									b as unknown as Tersed<CtaButtonContentItem>
								}
								className={clsx(
									`mb-2`,
									arr.length === 1 ? 'w-full' : null,
									index < arr.length - 1 && arr.length !== 1
										? 'mr-4'
										: null,
									removeBorders[0]?.codename === 'yes' ||
										stackButtons[0]?.codename === 'yes'
										? 'w-full'
										: null
								)}
								key={b.system.id}
								{...context}
							/>
						) : (
							<div
								className={clsx(
									// `inline-block` (not the default block-level `div`)
									// so this wrapper sits beside the CTA button instead
									// of forcing it onto a new line.
									`mb-2 inline-block`,
									arr.length === 1 ? 'w-full' : null,
									index < arr.length - 1 && arr.length !== 1
										? 'mr-4'
										: null,
									removeBorders[0]?.codename === 'yes' ||
										stackButtons[0]?.codename === 'yes'
										? 'w-full'
										: null,
									// Override MikMakButtonBlock's own `text-center` so its
									// button aligns left within this card only.
									'[&>div]:text-left',
									// MikMakButtonBlock wraps its button in a div with
									// `my-6 md:my-8` spacing and adds `mt-2` on the button
									// itself, meant for standalone use. Zero those out here
									// so the gap matches the CTA button's spacing, which
									// relies solely on this wrapper's own `mb-2`/`mr-4`.
									'[&>div]:my-0 [&_.swn-tag-wtb-btn]:mt-0',
									// The `w-full` rules above only size this wrapper, so
									// propagate full width down to the actual button too.
									(arr.length === 1 ||
										removeBorders[0]?.codename === 'yes' ||
										stackButtons[0]?.codename === 'yes') &&
										'[&_.swn-tag-wtb-btn]:w-full'
								)}
								data-kontent-item-id={b.system.id}
								key={b.system.id}
							>
								<MikMakButtonBlock
									block={
										b as unknown as Tersed<MikmakButtonContentItem>
									}
									{...context}
								/>
							</div>
						)
					)}
				</>
			</CardFooter>
		</Card>
	)
}
